import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

type AppRole = "owner" | "observer" | "caregiver" | "security_officer" | "careos_admin";

/**
 * Returns whether the current user has a given platform-wide role
 * (e.g. 'security_officer'), via the public.has_role RPC.
 *
 * Uses react-query so the result is cached across remounts and route
 * changes — preventing the menu/UI from flickering between mount and
 * the async role check resolving (same pattern as useCompany / usePatientRole).
 *
 * Returns:
 *  - `null` while loading on first run (no cached value yet)
 *  - `true` / `false` once resolved
 */
export function useHasRole(role: string): boolean | null {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ["has-role", user?.id, role],
    queryFn: async (): Promise<boolean> => {
      if (!user?.id) return false;
      const { data, error } = await supabase.rpc("has_role", {
        _user_id: user.id,
        _role: role as AppRole,
      });
      if (error) return false;
      return Boolean(data);
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  if (!user) return false;
  if (isLoading && data === undefined) return null;
  return data ?? false;
}
